/**
 * <TutorStudentTile> — un élève du live dans la pellicule du masteur : sa vidéo, et son mode dessous
 * (molecule).
 *
 * La pellicule du round 2 (Wajih, 02/11) remplace la carte élève : sa vidéo (`<TutorStudentVideo>` : son
 * prénom dessus, **une seule puce**, la plus urgente, le liseré vert pendant qu'il parle), et
 * Dialogue · Concentré. L'élève regardé est entouré de l'univers et s'ouvre sur place : `children` (sa liste
 * d'activités) se pose sous sa vidéo, dans le même cadre. Round 4 (Wajih : « on ne voit pas que le tout forme
 * un seul bloc ») : chaque élève est une carte — sa vidéo, sa bascule et son activité —, regardé ou non ; le
 * regardé prend le liseré de l'univers.
 *
 * Toucher la vidéo regarde l'élève (`onSelect`) : sa liste vient sous sa vidéo, et ce qu'il fait s'ouvre au
 * centre. Contrôlée : le mode et la sélection arrivent en props.
 *
 * Round 3 (Wajih) : un élève qu'on ne regarde pas n'a pas de liste, mais **une petite barre : l'activité qu'il
 * a devant lui** (`<TutorStudentActivityBar>`), à la couleur de sa matière ; la toucher l'ouvre au centre
 * (`onOpenActivity`).
 *
 * Weekly du 03/11 (W5, W9) : **Dialogue · Concentré** est un bouton incrusté dans la visio
 * (`<TutorSoundModeToggle>`) ; **la pastille rouge**, en haut à droite de la carte, dit qu'il y a quelque chose
 * à aller voir chez lui (`tutorStudentAttention` : une carte au bac « À traiter », ou des messages non lus) ;
 * des messages attendent : elle ouvre son fil du chat (`onOpenChat`).
 *
 * Daily du 04/11 (Wajih) :
 *  - **le signal reprend la place du chrono** (`<TutorStudentChips>` : la main en jaune, une vraie alerte en
 *    rouge qui clignote, le message non lu) ; la pastille rouge reste en plus ;
 *  - **`compact`** : un élève qu'on ne regarde pas a une carte plus petite que l'élève regardé — son signal en
 *    glyphe (comme l'ancienne pastille compacte) et sa bascule sur sa vidéo, sans sa liste. Test du 04/11 après-midi
 *    (Wajih) : la carte réduite reste sur **deux lignes**, sa vidéo puis, dessous, une ligne avec son activité ;
 *    la miniature à côté de l'activité laissait un blanc et un titre illisible ;
 *  - **sa main levée pendant que le masteur parle à un autre** (`student.handChoice`) : la fenêtre de l'ancienne
 *    interface (`CheckpointLaunchPopup`) sur sa carte — « <champion> lui demande ce qui se passe ? », « Oui, vas-y »
 *    (le champion part) ou « Non » (`onHandChoice`).
 *
 * Le live où « À traiter » occupe la colonne du champion : **la tuile porte le fil de la séance** (`student.thread`, le
 * `<TutorSessionThread>` sous la vidéo ; le toucher ouvre l'onglet « Fil » de l'élève au centre, `onOpenThread`), puis
 * la ligne de son activité (son titre seul) et **« Liste · n »**, qui déplie sur place la liste de l'élève (`list`) —
 * sur chaque tuile, regardée ou non. Daily du 05/11 : sous le fil, pas de place gardée pour le survol (sa ligne se pose
 * par-dessus la vidéo), puisque le toucher ouvre le « Fil ».
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Button } from "../../atoms/Buttons/Button"
import { pushButtonFaceHeight, type ButtonSize } from "../../atoms/Buttons/Button/Button"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Text } from "../../atoms/typography/Text"
import { slopTo } from "../../utils/touchTarget"
import { tutorStudentAttention } from "../TutorStudentChips"
import { TutorSessionThread } from "../TutorSessionThread"
import { TutorSoundModeToggle } from "../TutorSoundModeToggle"
import { TutorStudentActivityBar } from "../TutorStudentActivityBar"
import { TutorStudentVideo } from "../TutorStudentVideo"

/** Les deux réponses de l'ancienne fenêtre de la main levée (`CheckpointLaunchPopup`) : « Oui, vas-y », « Non ». */
export type TutorHandChoice = "send_sam" | "decline"

export type TutorStudentTileProps = {
    student: UITutorLiveStudent
    /**
     * Le rapport largeur / hauteur de sa vidéo : la pellicule le choisit selon la place. Absent : la carte
     * remplit ce qui la porte et sa vidéo prend ce qui reste, en grand (une case de la mosaïque).
     */
    videoAspect?: number
    /** C'est lui qu'on regarde : le cadre de l'univers. Défaut `false`. */
    selected?: boolean
    /**
     * Une carte réduite (un élève qu'on ne regarde pas, dans la pellicule) : son signal en glyphe sur sa vidéo, son
     * activité dessous. Défaut `false`.
     */
    compact?: boolean
    onSelect: () => void
    onChangeMode: (mode: UISoundMode) => void
    /** La bascule du mode part. Défaut `false`. */
    modePending?: boolean
    /** Sa vraie caméra (`VideoView` de LiveKit). Absente : sa photo, ou son visage caméra coupée. */
    video?: React.ReactNode
    /** Ce qui se pose sous sa vidéo : la liste d'activités de l'élève regardé. */
    children?: React.ReactNode
    /**
     * Sa liste, dépliée par « Liste · n » au bout de la ligne de son activité (le live). Présente : cette ligne se
     * montre aussi sur l'élève regardé, et « Liste · n » la déplie. Absente : la tuile d'avant (`children`).
     */
    list?: React.ReactNode
    /** Sa petite barre (un élève qu'on ne regarde pas) : ouvrir au centre l'activité qu'il a devant lui. */
    onOpenActivity?: (activityId: string) => void
    /** Son fil touché : ouvrir son onglet « Fil » au centre, à la ligne d'un point (`eventId`) ou sur maintenant. */
    onOpenThread?: (eventId?: string) => void
    /** Ses messages non lus : la pastille ouvre son fil du chat. Absent : la pastille ne se touche pas. */
    onOpenChat?: () => void
    /** Répondre à sa main levée (`student.handChoice`). Absent : pas de réponse sur la carte. */
    onHandChoice?: (choice: TutorHandChoice) => void
    /** Le prénom du champion qui aide en séance (le monde de jeu de l'espace) : « … lui demande ce qui se passe ? ». */
    helperName?: string
    testID?: string
}

export function TutorStudentTile(props: TutorStudentTileProps) {
    const {
        student,
        videoAspect,
        selected = false,
        compact = false,
        onSelect,
        onChangeMode,
        modePending = false,
        video,
        children,
        list,
        onOpenActivity,
        onOpenThread,
        onOpenChat,
        onHandChoice,
        helperName,
        testID,
    } = props
    const attention = tutorStudentAttention(student)
    const chat = (student.unread ?? 0) > 0 ? onOpenChat : undefined
    const fill = videoAspect == null
    // La bascule : `md` dans une case de la mosaïque, `sm` dans la pellicule ; sa marge au doigt se tire de sa face.
    const modeButtonSize: ButtonSize = fill ? "md" : "sm"
    const modeSize = pushButtonFaceHeight(modeButtonSize)
    const handChoice = student.handChoice === true && onHandChoice != null && helperName != null
    // État d'interface : la liste dépliée sous la ligne de son activité.
    const [listOpen, setListOpen] = useState(false)
    const live = list != null

    return (
        <View style={[styles.root(selected), fill && styles.fill]} testID={testID}>
            {/*
             * Sa vidéo est l'enfant direct de la carte : dans une case de la mosaïque, elle prend ce que la carte lui
             * laisse (`flex: 1`). Une vue de plus entre les deux, sans hauteur, l'écrasait à zéro (test du 04/11).
             */}
            <View style={fill ? styles.videoFill : styles.video(videoAspect)}>
                <Pressable
                    onPress={onSelect}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={[TEXTS.look(student.firstname), ...attention].join(", ")}
                    style={styles.press}
                >
                    <TutorStudentVideo
                        student={student}
                        size={fill ? "stage" : undefined}
                        video={video}
                        compactChip={compact}
                    />
                </Pressable>
                {/* Posé sur la vidéo, hors du bouton qui la regarde : un toucher bascule le mode, pas la carte. */}
                <View style={styles.mode(fill, modeSize)}>
                    <TutorSoundModeToggle
                        mode={student.soundMode}
                        onChange={onChangeMode}
                        pending={modePending}
                        disabled={student.presence !== "here"}
                        studentName={student.firstname}
                        size={modeButtonSize}
                    />
                </View>
            </View>
            {student.thread != null && <TutorSessionThread {...student.thread} onOpenThread={onOpenThread} />}
            {live ? (
                <View style={styles.current}>
                    <View style={styles.flex}>
                        <TutorStudentActivityBar student={student} onOpenActivity={onOpenActivity} />
                    </View>
                    <Pressable
                        onPress={() => setListOpen((open) => !open)}
                        accessibilityRole="button"
                        accessibilityState={{ expanded: listOpen }}
                        accessibilityLabel={TEXTS.listLabel(student.firstname, student.activities.length)}
                        hitSlop={LIST_SLOP}
                        style={styles.listToggle(listOpen)}
                    >
                        <Text size="text-xs" weight="semibold" color={listOpen ? "role-strong" : "text-muted"}>
                            {TEXTS.list(student.activities.length)}
                        </Text>
                    </Pressable>
                </View>
            ) : (
                !selected && <TutorStudentActivityBar student={student} onOpenActivity={onOpenActivity} />
            )}
            {live && listOpen && list}
            {handChoice && (
                <View style={styles.ask}>
                    <Text size="text-xs" weight="semibold" color="text-strong">
                        {TEXTS.ask(helperName)}
                    </Text>
                    <View style={styles.choices}>
                        <Button
                            label={TEXTS.yes}
                            size="sm"
                            onPress={() => onHandChoice("send_sam")}
                            accessibilityLabel={TEXTS.yesLabel(helperName, student.firstname)}
                        />
                        <Button
                            label={TEXTS.no}
                            size="sm"
                            variant="outline"
                            onPress={() => onHandChoice("decline")}
                            accessibilityLabel={TEXTS.noLabel(helperName, student.firstname)}
                        />
                    </View>
                </View>
            )}
            {children}
            {attention.length > 0 && (
                // Des messages attendent : la pastille ouvre son fil, d'un geste, comme la bulle d'avant ; sinon elle
                // laisse passer le toucher.
                <Pressable
                    onPress={chat}
                    disabled={chat == null}
                    pointerEvents={chat == null ? "none" : "auto"}
                    accessibilityRole={chat != null ? "button" : undefined}
                    accessibilityLabel={chat != null ? TEXTS.chat(student.firstname) : undefined}
                    hitSlop={chat != null ? DOT_SLOP : undefined}
                    style={styles.dot}
                >
                    <NotificationDot size={DOT_SIZE} />
                </Pressable>
            )}
        </View>
    )
}

const TEXTS = {
    list: (count: number) => `Liste · ${count}`,
    listLabel: (firstname: string, count: number) => `La liste de ${firstname}, ${countLabel(count, "activité")}`,
    look: (firstname: string) => `Regarder ${firstname}`,
    chat: (firstname: string) => `Les messages de ${firstname}`,
    // La question de l'ancienne fenêtre de la main levée (`LiveParticipant.js`, `CheckpointLaunchPopup`), sans accord
    // deviné pour l'élève.
    ask: (helper: string) => `${helper} lui demande ce qui se passe ?`,
    yes: "Oui, vas-y",
    yesLabel: (helper: string, firstname: string) => `Oui, ${helper} demande à ${firstname} ce qui se passe`,
    no: "Non",
    noLabel: (helper: string, firstname: string) => `Non, ${helper} ne va pas voir ${firstname}`,
}

const DOT_SIZE = 14
/** « Liste · n » : une ligne de texte, sa cible portée à celle d'un doigt. */
const LIST_SLOP = { top: 10, bottom: 10, left: 4, right: 4 }
/** Une pastille à toucher : la cible s'étend à celle d'un doigt. */
const DOT_SLOP = slopTo(DOT_SIZE)

const styles = StyleSheet.create((theme) => ({
    /** Un élève, un seul bloc (round 4) : sa carte, entourée de l'univers quand on le regarde. */
    root: (selected: boolean) => ({
        gap: theme.spacing["1"],
        padding: theme.spacing["2"],
        borderRadius: theme.radius.lg,
        borderWidth: 2,
        borderColor: selected ? theme.colors.role : theme.colors["border-subtle"],
        backgroundColor: theme.colors["surface-raised"],
    }),
    fill: { flex: 1, minHeight: 0 },
    video: (aspect: number | undefined) => ({ aspectRatio: aspect }),
    videoFill: { flex: 1, minHeight: 0, borderRadius: theme.radius.lg, overflow: "hidden" as const },
    press: { flex: 1, minHeight: 0 },
    /**
     * Le bouton de mode, au coin bas droit de la vidéo. Son conteneur s'élargit de la marge qui porte la
     * cible à celle d'un doigt (une zone de toucher ne déborde jamais de son parent), et recule d'autant :
     * le bouton reste à sa place.
     */
    mode: (fill: boolean, size: number) => ({
        position: "absolute" as const,
        padding: slopTo(size),
        right: theme.spacing[fill ? "3" : "1"] - slopTo(size),
        bottom: theme.spacing[fill ? "3" : "1"] - slopTo(size),
    }),
    /** Son activité puis « Liste · n », sur une ligne. */
    current: { flexDirection: "row" as const, alignItems: "center" as const, gap: theme.spacing["2"] },
    flex: { flex: 1, minWidth: 0 },
    listToggle: (open: boolean) => ({
        minHeight: 24,
        justifyContent: "center" as const,
        paddingHorizontal: theme.spacing["1"],
        borderRadius: theme.radius.sm,
        backgroundColor: open ? theme.colors["surface-subtle"] : "transparent",
    }),
    /** La question du champion et ses deux réponses, sous la vidéo. */
    ask: { gap: theme.spacing["1"] },
    /** Les deux réponses, côte à côte, qui passent à la ligne si la carte est étroite. */
    choices: { flexDirection: "row" as const, flexWrap: "wrap" as const, gap: theme.spacing["1"] },
    /** Il y a quelque chose à aller voir : le coin haut droit de la carte, à cheval sur son bord. */
    dot: { position: "absolute" as const, top: -theme.spacing["1"], right: -theme.spacing["1"] },
}))
